import React, {
  useState,
} from 'react'

import PropTypes from 'prop-types'

import { IntlProvider } from '@termly-web/common'

import Termly from 'termly-namespace'

import { LOCALE_STRINGS_TIMEOUT_MS } from './constants'

// Generating no box keeps this wrapper out of the layout it is standing
// in, which is how an earlier wrapper here broke layouts twice. The
// direction still reaches the children: that inherits down the element
// tree, not the box tree.
const SCOPED_ROOT_STYLE = {
  display: 'contents',
}


export default function ResourceBlockerIntlProvider({ children, isFirstPaintGated = true, rootElem, supportedLocales }) {
  // This was initially defined as a file constant, but the problem is that
  // Termly.apiBase wasn't getting set until after this file was loaded.
  const localeFileURLTemplate = `${ Termly.apiBase }/resource-blocker/i18n/:locale.json`

  const preferredLocales = window.navigator.languages

  const [scopedRootElem, setScopedRootElem] = useState(null)

  // Waiting is the default because every widget here renders translated
  // copy, and because they share one request for it: a widget that
  // opted out by omission would fetch on terms the waiting ones never
  // agreed to.
  //
  // Leaving the default on is only half of it. The waiting happens
  // wherever the caller puts its <FirstPaintGate>, so a caller that
  // places none paints in the default locale and repaints when the
  // strings land, whatever it passes here.
  const localeStringsTimeoutMS = ( isFirstPaintGated ) ? LOCALE_STRINGS_TIMEOUT_MS : void 0

  const renderProvider = (elem) => {
    if ( !elem ) {
      return null
    }

    return (
      <IntlProvider
        localeFileURLTemplate={ localeFileURLTemplate }
        localeStringsTimeoutMS={ localeStringsTimeoutMS }
        preferredLocales={ preferredLocales }
        rootElem={ elem }
        supportedLocales={ supportedLocales }
      >
        { children }
      </IntlProvider>
    )
  }

  if ( rootElem ) {
    return renderProvider(rootElem)
  }

  // A caller that names no element is rendering inside a page it does
  // not own, so the direction goes on an element holding nothing but
  // this widget — and not before that element exists.
  return (
    <div
      ref={ setScopedRootElem }
      style={ SCOPED_ROOT_STYLE }
    >
      { renderProvider(scopedRootElem) }
    </div>
  )
}

ResourceBlockerIntlProvider.propTypes = {
  supportedLocales: IntlProvider.propTypes.supportedLocales,

  children: PropTypes.node,
  isFirstPaintGated: PropTypes.bool,
  rootElem: IntlProvider.propTypes.rootElem,
}
